/**
 * JTBD Dashboard - data module.
 * CDN constant, tool URL builder, typed data loaders/curators.
 * All pure functions / module-scope data — no React.
 */
"use client";

import { ToolInfo, toolsRegistry } from "@/contexts/ToolsContext";
import { isAiReportToolId } from "@/libs/ai-report-tools";
import { buildPresetHref, getDefaultPresetTool } from "@/app/editor/data/ai-photo-editor";

// ── CDN ──
export const CDN_URL = "https://pfpmakerai-v3.sfo3.cdn.digitaloceanspaces.com";

// ── Tool URL builder ──
export function getToolHref(tool: ToolInfo): string {
  if (tool.toolType === "free-editor") return `/pfpmaker-editor/${tool.id}`;
  if (tool.id === "ai-avatars") return `/editor-v2/ai-avatars`;
  if (tool.id === "ai-headshots") return `/editor-v2/ai-headshots`;
  if (tool.id === "ai-photo-editor") return buildPresetHref(getDefaultPresetTool().id);
  if (isAiReportToolId(tool.id)) return `/editor-v2/${tool.id}`;
  if (tool.isAvailable) return `/editor/${tool.id}`;
  return `/tools/${tool.id}`;
}

// ── Typed JSON imports ──
export interface AvatarStyle {
  id: number;
  title: string;
  prompt: string;
  style_type: string;
  category: string;
  new?: boolean;
  pinTop?: boolean;
  femaleOnly?: boolean;
  seasonal?: boolean;
  image?: string;
  cdnId?: number;
}

/** Viral-tagged style row for AI Photo Editor Go Viral dashboard cards. */
export interface DashboardViralAvatarStyle {
  id: string;
  title: string;
  image: string;
}

interface SelfieStyleRow {
  id: string;
  title: string;
  prompt: string;
  image: string;
  tags?: string[];
}

const VIRAL_PROMPT_EXCLUDE = ["couple", "parents", "two "];

function isViralExcludedPrompt(prompt: string): boolean {
  const lower = prompt.toLowerCase();
  return VIRAL_PROMPT_EXCLUDE.some((word) => lower.includes(word));
}

export interface BackgroundCard {
  id: string;
  title: string;
  scene_description: string;
  orig_filename: string;
}

export interface OutfitCard {
  id: string;
  Outfit: string;
  Gender: string;
  Color: string;
  Category: string;
  Clothing: string;
}

export interface MakeupStyle {
  id: number;
  title: string;
  category: string;
  prompt: string;
  thumbnail: string;
  featured?: boolean;
}

export interface StyleCardItem {
  title: string;
  imageSrc: string;
  href: string;
  toolId: string;
  cardId: string;
}

// ── Headshot Looks (curated bundles: outfit + background + expression + pose) ──

export interface HeadshotLook {
  id: string;
  title: string;
  gender: "female" | "male";
  outfitId: string;
  backgroundId: string;
  expressionId: string;
  poseId: string;
}

import headshotLooksJson from "@/app/editor/data/headshot-looks.json";

const allHeadshotLooks: HeadshotLook[] = (headshotLooksJson as any).looks ?? [];

/** Composite thumbnail: full look (outfit + background + pose) on CDN. */
export function lookCompositeUrl(lookId: string): string {
  return `${CDN_URL}/ai-headshots/composites-v2/${lookId}.webp`;
}

/** All curated headshot looks. Cards open the blank generator; look-id is tracked on click, not applied as a preselect. */
export function getHeadshotLookCards(): Array<{
  id: string;
  title: string;
  imageSrc: string;
  href: string;
  gender: "female" | "male";
}> {
  return allHeadshotLooks
    .filter((look) => look.id && look.outfitId)
    .map((look) => ({
      id: look.id,
      title: look.title,
      imageSrc: lookCompositeUrl(look.id),
      href: "/editor-v2/ai-headshots",
      gender: look.gender,
    }));
}

const HUB_HEADSHOT_FACE_COUNT = 3;

/** Three faces in the same look — one set per hub card. */
function hubHeadshotCycleUrls(lookId: string, startOffset = 0): string[] {
  const frames = Array.from({ length: HUB_HEADSHOT_FACE_COUNT }, (_, i) => {
    const n = String(i + 1).padStart(2, "0");
    return `${CDN_URL}/ai-photo-editor/hub-headshots/${lookId}-${n}.webp`;
  });
  const start = startOffset % HUB_HEADSHOT_FACE_COUNT;
  return [...frames.slice(start), ...frames.slice(0, start)];
}

/** Hub headshot cards: same outfit on 3 faces, start frame rotated so the grid does not match. */
export function getHubHeadshotLookCards(): Array<{
  id: string;
  title: string;
  images: string[];
  href: string;
}> {
  const looks = allHeadshotLooks.filter((look) => look.id && look.outfitId);
  return looks.map((look, index) => ({
    id: look.id,
    title: look.title,
    images: hubHeadshotCycleUrls(look.id, index),
    href: "/editor-v2/ai-headshots",
  }));
}

import avatarStylesJson from "@/app/editor/data/ai-avatars.json";
import selfieStylesJson from "@/app/editor/data/selfie-styles.json";
import backgroundsJson from "@/app/editor/data/ai-headshots-BG.json";
import outfitsMJson from "@/app/editor/data/ai-headshots-M.json";
import outfitsFJson from "@/app/editor/data/ai-headshots-F.json";
import makeupStylesJson from "@/app/editor/data/ai-makeup.json";

const allAvatarStyles: AvatarStyle[] = (avatarStylesJson as any).styles ?? [];
const allBackgrounds: BackgroundCard[] = (backgroundsJson as any).backgrounds ?? [];
const allMaleOutfits: OutfitCard[] = (outfitsMJson as any).outfits ?? [];
const allFemaleOutfits: OutfitCard[] = (outfitsFJson as any).outfits ?? [];
const allMakeupStyles: MakeupStyle[] = (makeupStylesJson as any).styles ?? [];

// ── Helper to build outfit thumbnail URL ──
export function outfitThumbnailUrl(outfit: OutfitCard): string {
  const gender = outfit.id.startsWith("outfit_M_") ? "M" : "F";
  return `${CDN_URL}/ai-headshots/${gender}/${outfit.id}.png`;
}

// ── Curated sections ──

/** Professional Backgrounds: ~12 curated from ai-headshots-BG.json */
export function getCuratedBackgrounds(): BackgroundCard[] {
  const officeKeywords = /office|lobby|studio|workspace|boardroom|corporate|conference|professional/i;
  const officeFirst: BackgroundCard[] = [];
  const rest: BackgroundCard[] = [];

  for (const bg of allBackgrounds) {
    if (officeKeywords.test(bg.title) || officeKeywords.test(bg.scene_description)) {
      officeFirst.push(bg);
    } else {
      rest.push(bg);
    }
  }

  const slice = [...officeFirst, ...rest].slice(0, 12);
  return slice;
}

/** Business Outfits: ~12 (6F+6M interleaved) from Category === "Business" */
export function getCuratedBusinessOutfits(): OutfitCard[] {
  const fBusiness = allFemaleOutfits.filter((o) => o.Category === "Business");
  const mBusiness = allMaleOutfits.filter((o) => o.Category === "Business");

  const interleaved: OutfitCard[] = [];
  const max = Math.min(6, fBusiness.length, mBusiness.length);
  for (let i = 0; i < max; i++) {
    interleaved.push(fBusiness[i]);
    interleaved.push(mBusiness[i]);
  }

  return interleaved;
}

function brandingAvatarPool(): AvatarStyle[] {
  const filtered = allAvatarStyles.filter(
    (s) => s.category === "Professional" || s.category === "Personal Branding"
  );
  const pinned = filtered.filter((s) => s.pinTop || s.new);
  const rest = filtered.filter((s) => !s.pinTop && !s.new);
  return [...pinned, ...rest];
}

/** Avatar Professional & Personal Branding styles (~10) */
export function getCuratedAvatarProfessional(): AvatarStyle[] {
  return brandingAvatarPool().slice(0, 10);
}

const HUB_PHOTOSHOOT_SET_COUNT = 12;
const HUB_PHOTOSHOOT_FRAME_COUNT = 5;

/** One person, 5 lifestyle looks — start frame rotated so the grid does not match. */
function hubPhotoshootCycleUrls(setIndex: number, startOffset = 0): string[] {
  const set = String(setIndex + 1).padStart(2, "0");
  const frames = Array.from({ length: HUB_PHOTOSHOOT_FRAME_COUNT }, (_, i) => {
    const n = String(i + 1).padStart(2, "0");
    return `${CDN_URL}/ai-photo-editor/hub-photoshoot/set-${set}-${n}.webp`;
  });
  const start = startOffset % HUB_PHOTOSHOOT_FRAME_COUNT;
  return [...frames.slice(start), ...frames.slice(0, start)];
}

/** Hub pillar: AI Photoshoot looks with fade-cycle lifestyle sets. */
export function getHubBrandingLookCards(limit = 12): Array<{
  id: string;
  title: string;
  images: string[];
  href: string;
}> {
  const pool = brandingAvatarPool();
  const count = Math.min(limit, HUB_PHOTOSHOOT_SET_COUNT, pool.length);
  return pool.slice(0, count).map((style, index) => ({
    id: String(style.id),
    title: style.title,
    images: hubPhotoshootCycleUrls(index, index),
    href: `/editor-v2/ai-avatars?style=${style.id}`,
  }));
}

/** Avatar Viral & Instagram styles (~10) */
export function getCuratedAvatarViral(): AvatarStyle[] {
  const filtered = allAvatarStyles.filter(
    (s) => s.category === "Viral" || s.category === "Instagram" || s.category === "Trending"
  );
  const pinned = filtered.filter((s) => s.pinTop || s.new);
  const rest = filtered.filter((s) => !s.pinTop && !s.new);
  return [...pinned, ...rest].slice(0, 10);
}

/** AI Photo Editor Go Viral row — same Viral-tagged pool as ai-avatars-v2 (selfie styles). */
export function getPhotoEditorGoViralAvatarStyles(limit = 36): DashboardViralAvatarStyle[] {
  const viralPool = (selfieStylesJson as SelfieStyleRow[]).filter(
    (style) => style.title?.trim() && style.image && !isViralExcludedPrompt(style.prompt),
  );
  const isNew = (style: SelfieStyleRow) =>
    style.tags?.some((tag) => tag.trim().toLowerCase() === "new");
  const pinned = viralPool.filter(isNew);
  const rest = viralPool.filter((style) => !isNew(style));

  return [...pinned, ...rest].slice(0, limit).map((style) => ({
    id: style.id,
    title: style.title,
    image: style.image,
  }));
}

/** Style thumbnail URL (mirrors getPreviewImage from ai-avatars-v2.tsx) */
export function avatarThumbnailUrl(style: AvatarStyle): string {
  if (style.image) return style.image;
  return `${CDN_URL}/ai-avatars/F/${style.id}.png`;
}

/** Short outfit label for cards */
export function outfitShortLabel(outfit: OutfitCard): string {
  if (outfit.Color && outfit.Clothing) {
    return `${outfit.Color} ${outfit.Clothing}`;
  }
  const words = outfit.Outfit.split(" ");
  return words.slice(0, 5).join(" ");
}

// ── Content-row card builders ──

/** Known cartoonizer filename → label mapping */
const CARTOON_LABEL_MAP: Record<string, string> = {
  simpsons: "Simpsons",
  south_park: "South Park",
  rick_morty: "Rick & Morty",
  family_guy: "Family Guy",
  disney: "Disney",
  disney_classic: "Classic Disney",
  marvel: "Marvel",
  pixar: "Pixar",
};

/** Known anime filename → label mapping */
const ANIME_LABEL_MAP: Record<string, string> = {
  shonen: "Shonen",
  shojo: "Shojo",
  chibi: "Chibi",
  cyberpunk: "Cyberpunk",
};

/** Derive a readable label from a cartoonizer/anime filename slug */
function styleLabelFromFilename(path: string): string {
  // Extract slug from ".../cartoonizer/F/simpsons.png" or ".../anime/shonen.png"
  const namePart = path.split("/").pop()?.replace(/\.\w+$/, "") ?? "";
  return CARTOON_LABEL_MAP[namePart] ?? ANIME_LABEL_MAP[namePart] ?? titleCaseSlug(namePart);
}

function titleCaseSlug(slug: string): string {
  return slug
    .split(/[_-]+/)
    .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Cartoon & Anime Avatars row: cards from cartoonizer + anime CDN thumbs */
function getCartoonizerImages(): string[] {
  return [
    `${CDN_URL}/cartoonizer/F/simpsons.png`,
    `${CDN_URL}/cartoonizer/F/south_park.png`,
    `${CDN_URL}/cartoonizer/F/rick_morty.png`,
    `${CDN_URL}/cartoonizer/F/family_guy.png`,
    `${CDN_URL}/cartoonizer/F/disney.png`,
    `${CDN_URL}/cartoonizer/F/disney_classic.png`,
    `${CDN_URL}/cartoonizer/F/marvel.png`,
    `${CDN_URL}/cartoonizer/F/pixar.png`,
  ];
}

function getAnimeImages(): string[] {
  return [
    `${CDN_URL}/anime/shonen.png`,
    `${CDN_URL}/anime/shojo.png`,
    `${CDN_URL}/anime/chibi.png`,
    `${CDN_URL}/anime/cyberpunk.png`,
  ];
}

function getHairstyleImages(): string[] {
  return [
    `${CDN_URL}/hairstyle/F/straight_pixie_f.png`,
    `${CDN_URL}/hairstyle/F/bangs.png`,
    `${CDN_URL}/hairstyle/F/straight_bob_f.png`,
    `${CDN_URL}/hairstyle/F/curly_bob_f.png`,
    `${CDN_URL}/hairstyle/F/curly_bun_f.png`,
    `${CDN_URL}/hairstyle/F/wavy_ponytail_f.png`,
    `${CDN_URL}/hairstyle/F/straight_bun_f.png`,
    `${CDN_URL}/hairstyle/F/wavy_long_f.png`,
  ];
}

function getWardrobeImages(): string[] {
  return [
    `${CDN_URL}/ai-headshots/F/outfit_F_42.png`,
    `${CDN_URL}/ai-headshots/F/outfit_F_7.png`,
    `${CDN_URL}/ai-headshots/F/outfit_F_37.png`,
    `${CDN_URL}/ai-headshots/F/outfit_F_14.png`,
    `${CDN_URL}/ai-headshots/F/outfit_F_46.png`,
    `${CDN_URL}/ai-headshots/F/outfit_F_17.png`,
    `${CDN_URL}/ai-headshots/F/outfit_F_22.png`,
    `${CDN_URL}/ai-headshots/F/outfit_F_30.png`,
  ];
}

export function getCartoonAnimeCards(): StyleCardItem[] {
  const cartoonImages = getCartoonizerImages();
  const animeImages = getAnimeImages();

  const cartoonCards: StyleCardItem[] = cartoonImages.map((src) => ({
    title: styleLabelFromFilename(src),
    imageSrc: src,
    href: "/editor/cartoonizer",
    toolId: "cartoonizer",
    cardId: src.split("/").pop()?.replace(/\.\w+$/, "") ?? "cartoon",
  }));

  const animeCards: StyleCardItem[] = animeImages.map((src) => ({
    title: styleLabelFromFilename(src),
    imageSrc: src,
    href: "/editor/anime-styling",
    toolId: "anime-styling",
    cardId: src.split("/").pop()?.replace(/\.\w+$/, "") ?? "anime",
  }));

  return [...cartoonCards, ...animeCards];
}

/** Makeup Looks row: ~10 featured/trending makeup cards */
export function getMakeupLookCards(): StyleCardItem[] {
  // Pick featured first, then fill from Cultural & Trending, up to ~10
  const featured = allMakeupStyles.filter((s) => s.featured);
  const trending = allMakeupStyles.filter((s) => s.category === "Cultural & Trending" && !s.featured);
  const pool = [...featured, ...trending].slice(0, 10);

  return pool.map((style) => ({
    title: style.title,
    imageSrc: `${CDN_URL}/ai-makeup/${style.id}.png`,
    href: "/editor/ai-makeup",
    toolId: "ai-makeup",
    cardId: String(style.id),
  }));
}

/** Hairstyles & Hair Colors row: cards from change-hair CDN thumbs */
export function getHairstyleCards(): StyleCardItem[] {
  const hairImages = getHairstyleImages();

  return hairImages.map((src) => {
    const namePart = src.split("/").pop()?.replace(/\.\w+$/, "") ?? "hair";
    // Strip trailing _f / _m
    const cleaned = namePart.replace(/_[fm]$/, "");
    const label = titleCaseSlug(cleaned);
    return {
      title: label,
      imageSrc: src,
      href: "/editor/change-hair",
      toolId: "change-hair",
      cardId: namePart,
    };
  });
}

/** Virtual Wardrobe row: cards from change-clothes CDN thumbs (outfit images) */
export function getWardrobeCards(): StyleCardItem[] {
  const outfitImages = getWardrobeImages();
  // Build a lookup by id for faster access
  const outfitLookup = new Map<string, OutfitCard>();
  for (const o of allFemaleOutfits) outfitLookup.set(o.id, o);
  for (const o of allMaleOutfits) outfitLookup.set(o.id, o);

  return outfitImages.map((src) => {
    // Extract outfit id from path like .../ai-headshots/F/outfit_F_42.png
    const namePart = src.split("/").pop()?.replace(/\.\w+$/, "") ?? "";
    const outfit = outfitLookup.get(namePart);
    const label = outfit ? outfitShortLabel(outfit) : `Outfit Look ${namePart.replace(/\D/g, "")}`;
    return {
      title: label,
      imageSrc: src,
      href: "/editor/change-clothes",
      toolId: "change-clothes",
      cardId: namePart,
    };
  });
}

// ── Tool helpers ──

/** Look up a tool by id from the registry. Returns undefined if not found or filtered out. */
export function findTool(id: string): ToolInfo | undefined {
  const tool = toolsRegistry.find((t) => t.id === id);
  if (!tool) return undefined;
  if (tool.isAvailable === false) return undefined;
  if (id.endsWith("-dev")) return undefined;
  return tool;
}

/** Filter the registry for active tools by list of ids. */
export function getToolsByIds(ids: string[]): ToolInfo[] {
  return ids
    .map(findTool)
    .filter((t): t is ToolInfo => t !== undefined)
    .filter((t) => t.isAvailable !== false && !t.id.endsWith("-dev"));
}

// ── Tool row definitions (ids) ──

export const HEADSHOT_ESSENTIAL_TOOL_IDS = [
  "basic-headshots",
  "ai-background",
  "ai-branding",
  "add-logo-watermark",
];

// STYLE_TRANSFORM_TOOL_IDS removed — replaced by content rows in JtbdDashboard

export const BEAUTY_REPORT_TOOL_IDS = [
  "attractiveness-test",
  "color-analysis",
  "face-shape",
  "hair-analysis",
  "celebrity-lookalike",
  "dating-profile-score",
];

export const QUICK_EDIT_TOOL_IDS = [
  "ai-designer",
  "hd-detailer",
  "add-smile",
  "clear-skin",
  "remove-accessories",
  "age-filter",
  "gender-swap",
  "photo-filters",
  "add-accessories",
  "change-scene",
  "ai-makeup",
];

export const FREE_PFP_TOOL_IDS = [
  "pfpmaker-legacy",
  "remove-background",
  "professional-backgrounds",
  "creative-backgrounds",
  "color-gradient-backgrounds",
  "borders",
  "text",
  "effects",
  "decals",
];

// ── Free Studio composites (CSS-layered cards; assets from editor-embedded.pfpmaker.com) ──

const FREE_EDITOR_BASE = "https://editor-embedded.pfpmaker.com";

export interface FreeCompositeCardDef {
  title: string;
  spec: import("./JtbdCards").FreeCompositeSpec;
  href: string;
  cardId: string;
}

/** Row 1 tool cards (legacy + remove-bg + filters + effects + text) — existing images, no composites. */
export const FREE_STUDIO_TOOL_IDS = ["pfpmaker-legacy", "remove-background", "photo-filters", "effects", "text"];

/** Professional Backgrounds — real background assets, link to category. */
export function getFreeProfessionalBackgroundCards(): FreeCompositeCardDef[] {
  const bgs = ["office1.jpg", "office2.jpg", "office3.jpg", "bokeh1.jpg", "bokeh2.jpg", "bokeh3.jpg"];
  return bgs.map((file) => ({
    title: file.replace(/\.jpg$/, "").replace(/(\d+)/, " $1").replace(/^./, (c) => c.toUpperCase()),
    spec: { kind: "background", backgroundUrl: `${FREE_EDITOR_BASE}/assets/backgrounds/${file}` },
    href: "/pfpmaker-editor/professional-backgrounds",
    cardId: `bg-${file}`,
  }));
}

/** Creative Backgrounds — beach/glow assets. */
export function getFreeCreativeBackgroundCards(): FreeCompositeCardDef[] {
  const bgs = ["beach1.jpg", "beach2.jpg", "beach3.jpg", "beach4.jpg", "glow1.jpg", "glow2.jpg"];
  return bgs.map((file) => ({
    title: file.replace(/\.jpg$/, "").replace(/(\d+)/, " $1").replace(/^./, (c) => c.toUpperCase()),
    spec: { kind: "background", backgroundUrl: `${FREE_EDITOR_BASE}/assets/backgrounds/${file}` },
    href: "/pfpmaker-editor/creative-backgrounds",
    cardId: `bg-${file}`,
  }));
}

/** Color & Gradient Backgrounds — CSS gradients (subset of the free editor's 49). */
export function getFreeGradientCards(): FreeCompositeCardDef[] {
  const gradients: Array<{ name: string; css: string }> = [
    { name: "Sunset", css: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)" },
    { name: "Ocean", css: "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)" },
    { name: "Purple Dream", css: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
    { name: "Peach", css: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)" },
    { name: "Mint", css: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)" },
    { name: "Rose", css: "linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)" },
  ];
  return gradients.map((g) => ({
    title: g.name,
    spec: { kind: "gradient", gradientCss: g.css },
    href: "/pfpmaker-editor/color-gradient-backgrounds",
    cardId: `grad-${g.name.toLowerCase().replace(/\s+/g, "-")}`,
  }));
}

/** Borders — ring colors paired with a complementary soft background inside the circle. */
export const HUB_FREE_BORDER_FRAMES = [
  { name: "Brand Purple", color: "#5956E9", bg: "linear-gradient(160deg, #eef0ff 0%, #dcd9fb 100%)" },
  { name: "Coral", color: "#ff6b6b", bg: "linear-gradient(160deg, #fff1f0 0%, #ffd9d6 100%)" },
  { name: "Gold", color: "#f7b733", bg: "linear-gradient(160deg, #fff8e7 0%, #ffe9bf 100%)" },
  { name: "Teal", color: "#38b2ac", bg: "linear-gradient(160deg, #ecfdf9 0%, #c9f2ec 100%)" },
  { name: "Hot Pink", color: "#ec4899", bg: "linear-gradient(160deg, #fff0f7 0%, #ffd3ea 100%)" },
  { name: "Navy", color: "#1e3a5f", bg: "linear-gradient(160deg, #eaf0f8 0%, #cdd9ea 100%)" },
] as const;

export function getFreeBorderCards(): FreeCompositeCardDef[] {
  return HUB_FREE_BORDER_FRAMES.map((b) => ({
    title: b.name,
    spec: { kind: "border", borderColor: b.color, borderBg: b.bg },
    href: "/pfpmaker-editor/borders",
    cardId: `border-${b.name.toLowerCase().replace(/\s+/g, "-")}`,
  }));
}

/** Shadows — real shadow decal PNGs layered behind the portrait. */
export function getFreeShadowCards(): FreeCompositeCardDef[] {
  const shadows = ["shadow_01.png", "shadow_02.png", "shadow_03.png", "shadow_04.png", "shadow_05.png", "shadow_06.png"];
  return shadows.map((file, i) => ({
    title: `Shadow ${i + 1}`,
    spec: { kind: "shadow", shadowUrl: `${FREE_EDITOR_BASE}/assets/shadows/${file}` },
    href: "/pfpmaker-editor/effects",
    cardId: `shadow-${i + 1}`,
  }));
}

function freeEditorBg(file: string): string {
  return `${FREE_EDITOR_BASE}/assets/backgrounds/${file}`;
}

function svgFillDataUri(svgBody: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="600">${svgBody}</svg>`
  )}`;
}

function gradientFillUri(from: string, to: string): string {
  return svgFillDataUri(
    `<defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="${from}"/><stop offset="100%" stop-color="${to}"/></linearGradient></defs><rect width="100%" height="100%" fill="url(#g)"/>`
  );
}

function solidFillUri(color: string): string {
  return svgFillDataUri(`<rect width="100%" height="100%" fill="${color}"/>`);
}

const HUB_FREE_CUTOUTS = [
  `${FREE_EDITOR_BASE}/demo/demo1.png`,
  `${FREE_EDITOR_BASE}/demo/demo2.png`,
  `${FREE_EDITOR_BASE}/demo/demo3.png`,
] as const;

/**
 * One hub card per free tool. Background/gradient/border cards fade the
 * treatment and keep a demo cutout on top (same composites as Free Studio).
 */
export function getHubFreeToolCards(): Array<{
  id: string;
  toolId: string;
  title: string;
  images: string[];
  href: string;
  overlaySrc?: string;
  borderFrames?: Array<{ color: string; bg: string }>;
}> {
  const specs: Array<{
    id: string;
    toolId?: string;
    title: string;
    images: string[];
    overlaySrc?: string;
    borderFrames?: Array<{ color: string; bg: string }>;
  }> = [
    {
      id: "creative-backgrounds",
      title: "Creative Backgrounds",
      images: ["beach1.jpg", "beach2.jpg", "beach3.jpg"].map(freeEditorBg),
      overlaySrc: HUB_FREE_CUTOUTS[0],
    },
    {
      id: "color-gradient-backgrounds",
      title: "Color & Gradient Backgrounds",
      images: [
        gradientFillUri("#f6d365", "#fda085"),
        gradientFillUri("#a1c4fd", "#c2e9fb"),
        gradientFillUri("#667eea", "#764ba2"),
        gradientFillUri("#84fab0", "#8fd3f4"),
      ],
      overlaySrc: HUB_FREE_CUTOUTS[1],
    },
    {
      id: "plain-background",
      toolId: "color-gradient-backgrounds",
      title: "Color Backgrounds",
      images: ["#5956E9", "#ff6b6b", "#f7b733", "#38b2ac"].map(solidFillUri),
      overlaySrc: HUB_FREE_CUTOUTS[2],
    },
    {
      id: "professional-backgrounds",
      title: "Photo Backgrounds",
      images: ["office1.jpg", "office2.jpg", "bokeh1.jpg", "bokeh2.jpg"].map(freeEditorBg),
      overlaySrc: HUB_FREE_CUTOUTS[1],
    },
    {
      id: "borders",
      title: "Borders & Frames",
      images: [],
      overlaySrc: HUB_FREE_CUTOUTS[0],
      borderFrames: HUB_FREE_BORDER_FRAMES.map((frame) => ({
        color: frame.color,
        bg: frame.bg,
      })),
    },
    {
      id: "remove-background",
      title: "Remove Background",
      images: [...HUB_FREE_CUTOUTS],
    }
  ];

  return specs.flatMap((spec) => {
    const tool = findTool(spec.toolId ?? spec.id);
    if (!tool) return [];
    return [
      {
        id: spec.id,
        toolId: spec.toolId ?? spec.id,
        title: spec.title,
        images: spec.images,
        href: getToolHref(tool),
        overlaySrc: spec.overlaySrc,
        borderFrames: spec.borderFrames,
      },
    ];
  });
}
